import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import sensorService from '../services/sensorService';

// Get all sensors with smart caching
export const getSensors = createAsyncThunk(
  'sensors/getSensors',
  async (forceRefresh = false, { getState, rejectWithValue }) => {
    try {
      const state = getState().sensors;
      const now = Date.now();

      // Temporary logging to verify caching
      console.log('🔍 getSensors called with forceRefresh:', forceRefresh);
      console.log('📊 Sensors cache status:', {
        cached: state.cached,
        lastFetched: state.lastFetched,
        age: state.lastFetched ? (now - state.lastFetched) / 1000 : 'never',
        timeout: state.cacheTimeout / 1000
      });

      // Check if we should use cached data
      if (!forceRefresh && state.cached && state.lastFetched &&
          (now - state.lastFetched) < state.cacheTimeout) {
        console.log('✅ Using cached sensors data');
        // Return cached data with special flag
        return { data: state.sensors, fromCache: true };
      }

      console.log('🌐 Fetching fresh sensors data');
      // Fetch fresh data
      const sensors = await sensorService.getAll();
      return sensors;
    } catch (error) {
      console.error('sensorsSlice: Error fetching sensors:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get a single sensor
export const getSensor = createAsyncThunk(
  'sensors/getSensor',
  async (id, { rejectWithValue }) => {
    try {
      return await sensorService.getById(id);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Create a new sensor
export const createSensor = createAsyncThunk(
  'sensors/createSensor',
  async (sensorData, { rejectWithValue }) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('sensorsSlice: Creating sensor with data:', sensorData);
      }
      const result = await sensorService.create(sensorData);
      if (process.env.NODE_ENV === 'development') {
        console.log('sensorsSlice: Sensor created successfully:', result);
      }
      return result;
    } catch (error) {
      console.error('sensorsSlice: Error creating sensor:', error);

      // Extract the error message
      let errorMessage = error.message;

      // If there's a response with data
      if (error.response?.data) {
        if (process.env.NODE_ENV === 'development') {
          console.error('sensorsSlice: Error response data:', error.response.data);
        }

        // Handle different error data formats
        if (typeof error.response.data === 'object') {
          // If it's an object, try to extract the message or error field
          errorMessage = error.response.data.message || error.response.data.error || JSON.stringify(error.response.data);
        } else if (typeof error.response.data === 'string') {
          // If it's a string, use it directly
          errorMessage = error.response.data;
        }
      }

      return rejectWithValue(errorMessage);
    }
  }
);

// Update a sensor
export const updateSensor = createAsyncThunk(
  'sensors/updateSensor',
  async ({ id, sensorData }, { rejectWithValue }) => {
    try {
      return await sensorService.update(id, sensorData);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Delete a sensor
export const deleteSensor = createAsyncThunk(
  'sensors/deleteSensor',
  async (id, { rejectWithValue }) => {
    try {
      await sensorService.remove(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  sensors: [],
  currentSensor: null,
  loading: false,
  error: null,
  success: false,
  cached: false,
  lastFetched: null,
  cacheTimeout: 5 * 60 * 1000, // 5 minutes
};

const sensorsSlice = createSlice({
  name: 'sensors',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    setCurrentSensor: (state, action) => {
      state.currentSensor = action.payload;
    },
    clearCurrentSensor: (state) => {
      state.currentSensor = null;
    },
    invalidateCache: (state) => {
      state.cached = false;
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all sensors
      .addCase(getSensors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSensors.fulfilled, (state, action) => {
        // Silent operation
        state.loading = false;
        // Handle both cached and fresh data responses
        if (action.payload && action.payload.data) {
          state.sensors = action.payload.data;
        } else {
          state.sensors = action.payload;
        }
        state.cached = true;
        state.lastFetched = Date.now();
        state.error = null;
        // Don't set success for GET operations - only for CRUD operations
        // state.success should remain unchanged
      })
      .addCase(getSensors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get single sensor
      .addCase(getSensor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSensor.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSensor = action.payload;
      })
      .addCase(getSensor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create sensor
      .addCase(createSensor.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createSensor.fulfilled, (state, action) => {
        state.loading = false;
        state.sensors.push(action.payload);
        state.success = true;
        // Invalidate cache since data changed
        state.cached = false;
        state.lastFetched = null;
      })
      .addCase(createSensor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Update sensor
      .addCase(updateSensor.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateSensor.fulfilled, (state, action) => {
        state.loading = false;
        state.sensors = state.sensors.map(sensor =>
          sensor.id === action.payload.id ? action.payload : sensor
        );
        state.currentSensor = action.payload;
        state.success = true;
      })
      .addCase(updateSensor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Delete sensor
      .addCase(deleteSensor.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteSensor.fulfilled, (state, action) => {
        state.loading = false;
        state.sensors = state.sensors.filter(sensor => sensor.id !== action.payload);
        state.success = true;
      })
      .addCase(deleteSensor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
  },
});

export const {
  clearError,
  clearSuccess,
  setCurrentSensor,
  clearCurrentSensor,
} = sensorsSlice.actions;

export default sensorsSlice.reducer;